import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getOwnershipHistoryOfFolder } from '../../Services/Search.services';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import SkeletonComponent from '../SkeletonComponent';
import { AvlTable } from './Table/Table';
import { datetimeToFormattedString } from '../../Utils/Utils';

const columns = {
    performedAt: 'Performed At',
    operation: 'Operation',
    oldOwner: 'Old Owner',
    newOwner: 'New Owner',
    statusCode: 'Status Code',
};

function ChangeOwnerTable({ folderId }) {
    const [records, setRecords] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [countRowsPerPage, setCountRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(20);

    useEffect(() => {
        getRecords(page);
    }, []);

    const getRecords = async (page, rowsPerPage) => {
        setIsLoading(true);
        rowsPerPage ??= countRowsPerPage;

        getOwnershipHistoryOfFolder(folderId)
            .then((response) => {
                const allRecords = response.data['ownership_history'];
                const rowsCount = allRecords.length;
                const pagesCount = Math.ceil((rowsCount / rowsPerPage));

                setTotalRows(rowsCount);
                setRecords(allRecords);
                setPageCount(pagesCount);
            })
            .finally(() => setIsLoading(false))
            .catch(() => {
                setRecords([]);
                setPageCount(1);
            });
    };

    const handlePageChange = (event, value) => {
        setIsLoading(true);
        setPage(value);
        getRecords(value);
    };

    const handleChangeRowsPerPagePage = (rowsPerPage) => {
        const initialPage = 1;
        setCountRowsPerPage(rowsPerPage);
        setPage(initialPage);
        getRecords(initialPage, rowsPerPage);
    };

    const tableHeaders = [
        {
            label: columns.operation,
            align: 'left',
        },
        {
            label: columns.performedAt,
            align: 'left',
        },
        {
            label: columns.oldOwner,
            align: 'left',
        },
        {
            label: columns.newOwner,
            align: 'left',
        },
        {
            label: columns.statusCode,
            align: 'center',
        },
    ];

    const convertToCells = (row, rowIndex) => [
        {
            data: row.operation || 'N/A',
        },
        {
            style: { minWidth: '90px' },
            data: datetimeToFormattedString(row.performed_at, 'None'),
        },
        {
            align: 'left',
            style: { minWidth: '150px' },
            data: row?.old_owner || 'N/A',
        },
        {
            align: 'left',
            style: { minWidth: '150px' },
            data: row?.new_owner || 'N/A',
        },
        {
            align: 'center',
            style: { width: '80px' },
            data: row.status_code || 'None',
        },
    ];

    const tableRows = records.map((row, rowIndex) => ({
        cells: convertToCells(row, rowIndex),
    }));

    return (
        <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {
                isLoading
                    ? (<SkeletonComponent />)
                    : (
                        records.length > 0
                            ? (
                                <AvlTable
                                    headers={tableHeaders}
                                    rows={tableRows}
                                    page={page}
                                    pageCount={pageCount}
                                    isRowsPerPageOptionsVisible
                                    onPageChange={handlePageChange}
                                    rowsPerPage={countRowsPerPage}
                                    rowsCount={totalRows}
                                    onChangeRowsPerPage={handleChangeRowsPerPagePage}
                                />
                            )
                            : (
                                <Stack sx={{ width: '100%' }} spacing={2}>
                                    <Alert severity="warning">No change-owner records found!</Alert>
                                </Stack>
                            )
                    )
            }
        </div>
    );
}

ChangeOwnerTable.propTypes = {
    folderId: PropTypes.string.isRequired,
};

export default ChangeOwnerTable;